import { transformUnits } from '@/helpers/unit-formatter/unit-formatter';
import { FormattedUnitLabels, UnitValue } from '@/helpers/unit-formatter/unit-formatter.types';
import {
  formatNumber,
  toNumber,
  unitValueToFormattedUnitLabels,
} from '@/helpers/unit-formatter/unit-formatter.utils';
import { isNullOrUndefined } from '@/utils/is-null-or-undefined';

/**
 * Formatter for unitless or non-numeric values.
 * Handles null/undefined values by passing them through without formatting,
 * while numeric values are formatted with standard number formatting.
 *
 * @param {UnitValue} unitValue - The value and units to format
 * @param {any} unitValue.value - The value to format (can be any type)
 * @param {string} [unitValue.units] - The current units of the value
 * @param {string} [displayUnits] - Optional target units to transform to before formatting
 * @returns {FormattedUnitLabels} Formatted labels containing:
 *   - valueLabel: The formatted value or original value if non-numeric
 *   - unitsLabel: The unit string
 *   - valueUnitsLabel: Combined value and units with proper spacing
 *
 * @example
 * formatNone({ value: 'Active', units: '' })
 * // Returns: { valueLabel: 'Active', unitsLabel: '', valueUnitsLabel: 'Active' }
 *
 * @example
 * formatNone({ value: null, units: 'status' })
 * // Returns: { valueLabel: '--', unitsLabel: 'status', valueUnitsLabel: '-- status' }
 *
 * @example
 * formatNone({ value: 42, units: 'count' })
 * // Returns: { valueLabel: '42', unitsLabel: 'count', valueUnitsLabel: '42 count' }
 */
export const formatNone = (unitValue: UnitValue, displayUnits?: string): FormattedUnitLabels => {
  const transformed = transformUnits(unitValue, displayUnits);
  const value = toNumber(transformed.value);

  if (isNullOrUndefined(value)) {
    return unitValueToFormattedUnitLabels(transformed);
  }

  return unitValueToFormattedUnitLabels({
    value: formatNumber(transformed.value),
    units: transformed.units,
  });
};
